import React from 'react';
import {FlatList, Pressable, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import CCText from '@/Atoms/CCText';
import {useInterClubBoard} from '@/Hooks/ClubsApi/useClubsQueries';
import {InterClubRow} from '@/Hooks/ClubsApi/types';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import InterClubBoardSkeleton from './InterClubBoardSkeleton';
import ClubBadge from './ClubBadge';
import {BADGES, DEFAULT_BADGE_BG} from '../badgeOptions';
import type {PieceKey} from '../clubPieces';
import {ScreenName} from '@/navigators/StackConstants';

const BROWN = '#4D3F37';
const INK = '#262322';
const MUTED = '#888280';

const pieceFor = (badgeId?: string): PieceKey =>
  (BADGES.find(badge => badge.id === badgeId)?.piece ?? 'pawn') as PieceKey;

const rankTheme = [
  {background: '#FFF4C7', border: '#E7BA2E', foreground: '#9B6C00'},
  {background: '#F2F3F4', border: '#C7CBCF', foreground: '#697077'},
  {background: '#F9E9DD', border: '#D99A6C', foreground: '#A66034'},
];

function TrendBadge({trend, delta}: Pick<InterClubRow, 'trend' | 'delta'>) {
  if (trend === 'new') {
    return (
      <View
        style={{
          backgroundColor: '#EEE9FF',
          borderRadius: 10,
          paddingHorizontal: 7,
          paddingVertical: 3,
        }}>
        <CCText style={{fontSize: 9.5, fontWeight: '700', color: '#7255B5'}}>
          NEW
        </CCText>
      </View>
    );
  }
  if (delta === 0 || trend === 'same') return null;
  const up = trend === 'up';
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        backgroundColor: up ? '#E8F7F0' : '#FCEDEB',
        borderRadius: 10,
        paddingHorizontal: 6,
        paddingVertical: 3,
      }}>
      <MaterialCommunityIcons
        name={up ? 'trending-up' : 'trending-down'}
        size={11}
        color={up ? '#1E9E63' : '#C8433B'}
      />
      <CCText
        style={{
          fontSize: 9.5,
          fontWeight: '700',
          color: up ? '#1E9E63' : '#C8433B',
        }}>
        {up ? '+' : '−'}
        {Math.abs(delta)}
      </CCText>
    </View>
  );
}

export default function InterClubBoard({
  scope,
  limit = 3,
  embedded = false,
}: {
  scope: 'public' | 'private';
  limit?: number;
  embedded?: boolean;
}) {
  const {data, isLoading} = useInterClubBoard(scope, 'all', limit);
  const {isMobileOrTab} = useMediaQuery();
  const navigation = useNavigation<any>();
  const compact = isMobileOrTab;

  if (isLoading || !data) {
    return <InterClubBoardSkeleton count={limit} elevated={!embedded} />;
  }

  const renderRow = (item: InterClubRow, index: number) => {
    const rank = index + 1;
    const podium = rankTheme[index];
    const leader = rank === 1;
    return (
      <Pressable
        testID={`inter-club-row-${item.club_id}`}
        accessibilityRole="button"
        accessibilityLabel={`Rank ${rank}, ${item.name}, ${item.points} points`}
        onPress={() =>
          navigation.navigate(ScreenName.ClubProfile, {clubId: item.club_id})
        }
        style={({hovered, pressed}: any) => [
          {
            flexDirection: 'row',
            alignItems: 'center',
            gap: compact ? 8 : 11,
            minHeight: compact ? 60 : 72,
            paddingVertical: compact ? 8 : 10,
            paddingHorizontal: compact ? 9 : 12,
            borderRadius: compact ? 13 : 16,
            backgroundColor: leader ? '#FFF8E6' : '#F8F6F1',
            marginBottom: compact ? 7 : 9,
            cursor: 'pointer',
          },
          hovered && {backgroundColor: leader ? '#FCEFC4' : '#F1EDE3'},
          pressed && {opacity: 0.9},
        ]}>
        <View
          testID={`inter-club-rank-${item.club_id}`}
          style={{
            width: compact ? 25 : 30,
            height: compact ? 25 : 30,
            borderRadius: 15,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: podium?.background ?? '#F5F1E7',
            borderWidth: podium ? 1 : 0,
            borderColor: podium?.border ?? 'transparent',
          }}>
          <CCText
            style={{
              fontSize: compact ? 11 : 13,
              fontWeight: '800',
              color: podium?.foreground ?? MUTED,
            }}>
            {rank}
          </CCText>
        </View>

        <View
          testID={`inter-club-badge-${item.club_id}`}
          accessibilityLabel={`${item.name} club badge`}>
          <ClubBadge
            piece={pieceFor(item.badge_id)}
            color={item.background_color || DEFAULT_BADGE_BG}
            size={compact ? 31 : 42}
            radius={compact ? 8 : 11}
          />
        </View>

        <View style={{flex: 1, minWidth: 0}}>
          <View
            testID={`inter-club-name-row-${item.club_id}`}
            style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
            <CCText
              numberOfLines={compact ? 2 : 1}
              style={{
                flexShrink: 1,
                fontSize: compact ? 13 : 15.5,
                lineHeight: compact ? 16 : 19,
                fontWeight: leader ? '700' : '600',
                color: INK,
              }}>
              {item.name}
            </CCText>
            {item.official ? (
              <View
                testID={`inter-club-official-${item.club_id}`}
                accessibilityLabel="Official club">
                <MaterialCommunityIcons
                  name="check-decagram"
                  size={compact ? 16 : 18}
                  color="#D2A51F"
                />
              </View>
            ) : null}
            {item.visibility === 'private' ? (
              <View
                testID={`inter-club-private-${item.club_id}`}
                accessibilityLabel="Private club"
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 9,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#F3EEFF',
                }}>
                <MaterialCommunityIcons name="lock" size={11} color="#6948C4" />
              </View>
            ) : null}
          </View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 3,
              marginTop: 2,
            }}>
            <CCText style={{fontSize: compact ? 10.5 : 11.5, color: MUTED}}>
              {item.players} players
            </CCText>
            {leader && !compact ? (
              <CCText
                style={{
                  fontSize: compact ? 9.5 : 10.5,
                  fontWeight: '700',
                  color: '#A4770A',
                }}>
                · Current leader
              </CCText>
            ) : null}
          </View>
        </View>

        <View style={{alignItems: 'flex-end', gap: 5}}>
          <View
            style={{
              backgroundColor: leader ? '#F8D866' : '#E7F5F1',
              borderRadius: 12,
              paddingHorizontal: compact ? 7 : 9,
              paddingVertical: 4,
            }}>
            <CCText
              style={{
                fontSize: compact ? 12 : 14,
                fontWeight: '800',
                color: leader ? '#6D5000' : '#277A70',
              }}>
              {item.points} pts
            </CCText>
          </View>
          <TrendBadge trend={item.trend} delta={item.delta} />
        </View>
      </Pressable>
    );
  };

  return (
    <View
      testID="inter-club-board"
      style={{
        width: '100%',
        alignSelf: 'stretch',
        backgroundColor: '#FFFFFF',
        borderRadius: compact ? 18 : 22,
        paddingHorizontal: compact ? 12 : 18,
        paddingTop: compact ? 14 : 20,
        paddingBottom: compact ? 8 : 12,
        shadowColor: BROWN,
        shadowOffset: {width: 0, height: 5},
        shadowOpacity: embedded ? 0 : 0.08,
        shadowRadius: embedded ? 0 : 16,
        elevation: embedded ? 0 : 3,
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: compact ? 10 : 14,
          marginBottom: compact ? 13 : 18,
        }}>
        <View
          style={{
            width: compact ? 40 : 50,
            height: compact ? 40 : 50,
            borderRadius: compact ? 14 : 18,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#EF6B2E',
          }}>
          <CCText
            style={{
              fontSize: compact ? 20 : 25,
              lineHeight: compact ? 24 : 29,
            }}>
            🏆
          </CCText>
        </View>
        <View style={{flex: 1, minWidth: 0}}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 7}}>
            <CCText
              testID="inter-club-title"
              numberOfLines={compact ? 2 : 1}
              style={{
                flexShrink: 1,
                fontSize: compact ? 15 : 19,
                fontWeight: '800',
                color: INK,
              }}>
              Top Performing Clubs
            </CCText>
            <View
              style={{
                borderRadius: 12,
                paddingHorizontal: 7,
                paddingVertical: 3,
                backgroundColor: '#E8F7F0',
              }}>
              <CCText
                style={{
                  color: '#23835A',
                  fontSize: compact ? 8.5 : 9.5,
                  fontWeight: '800',
                  letterSpacing: 0.4,
                }}>
                ALL TIME
              </CCText>
            </View>
          </View>
          {!compact ? (
            <CCText
              style={{
                fontSize: 12.5,
                fontWeight: '700',
                color: BROWN,
                marginTop: 2,
              }}>
              Club leaderboard
            </CCText>
          ) : null}
          <CCText
            testID="inter-club-description"
            numberOfLines={compact ? 2 : 1}
            style={{
              fontSize: compact ? 9.5 : 11.5,
              lineHeight: compact ? 13 : 15,
              color: MUTED,
              marginTop: compact ? 3 : 2,
            }}>
            Total points earned by every club member
          </CCText>
        </View>
      </View>
      {(data.rows ?? []).length ? (
        <>
          {!compact ? <View
            testID="inter-club-column-headings"
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingHorizontal: compact ? 9 : 12,
              marginBottom: 8,
            }}>
            <CCText
              style={{
                fontSize: compact ? 9 : 10.5,
                fontWeight: '700',
                color: MUTED,
                letterSpacing: 0.5,
              }}>
              CLUB
            </CCText>
            <CCText
              style={{
                fontSize: compact ? 9 : 10.5,
                fontWeight: '700',
                color: MUTED,
                letterSpacing: 0.5,
              }}>
              POINTS
            </CCText>
          </View> : null}
          <FlatList
            data={data.rows}
            renderItem={({item, index}) => renderRow(item, index)}
            keyExtractor={item => String(item.club_id)}
            scrollEnabled={false}
            contentContainerStyle={{paddingBottom: 2}}
          />
        </>
      ) : (
        <View
          style={{
            alignItems: 'center',
            paddingVertical: 24,
            paddingHorizontal: 16,
          }}>
          <CCText style={{fontSize: 30, lineHeight: 34}}>🏆</CCText>
          <CCText
            style={{
              fontSize: 13,
              fontWeight: '700',
              color: BROWN,
              marginTop: 7,
            }}>
            The race starts here
          </CCText>
          <CCText
            style={{
              fontSize: 11,
              color: MUTED,
              marginTop: 3,
              textAlign: 'center',
            }}>
            Club activity will appear as members earn points.
          </CCText>
        </View>
      )}
    </View>
  );
}
